import React from 'react';

import { Popover } from '@mui/material';

import SystemDesignButton from './Button';
import { DARK_RED, GREY, RED, SCOUT_BLUE } from '../../ThemeContext/constants';

export interface OverflowMenuItem {
  id: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  className?: string; // needed to preserve Cypress classNames
}

export interface OverflowMenuProps {
  id: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  items: OverflowMenuItem[];
}

const styles = {
  menu: {
    '& .MuiPaper-root': {
      display: 'flex',
      flexDirection: 'column',
      rowGap: '11px',
      // Symmetric: with no right padding, any label wider than minWidth runs
      // flush to the paper edge. Short labels hid it because minWidth absorbed
      // them, so it only surfaced once a longer item was added.
      padding: '14px 20px',
      borderRadius: '6px',
      minWidth: '153px',
      marginTop: '6px'
    }
  },
  item: {
    justifyContent: 'flex-start',
    minWidth: 'auto',
    minHeight: 'auto',
    height: 'auto',
    padding: 0,
    fontSize: '14px',
    fontWeight: 'bold',
    color: GREY,
    '&:hover': {
      color: SCOUT_BLUE,
      backgroundColor: 'transparent'
    }
  },
  destructiveItem: {
    justifyContent: 'flex-start',
    minWidth: 'auto',
    minHeight: 'auto',
    height: 'auto',
    padding: 0,
    fontSize: '14px',
    fontWeight: 'bold',
    color: RED,
    '&:hover': {
      color: DARK_RED,
      backgroundColor: 'transparent'
    }
  }
};

// Item clicks do not call onClose. The caller closes the menu, then runs the action.
const OverflowMenu = ({
  id,
  open,
  anchorEl,
  onClose,
  items
}: OverflowMenuProps): React.ReactElement => (
  <Popover
    anchorEl={anchorEl}
    id={id}
    onClose={onClose}
    open={open}
    sx={styles.menu}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
  >
    {items.map((item) => (
      <SystemDesignButton
        key={item.id}
        fullWidth
        buttonVariant="text"
        className={item.className}
        disabled={item.disabled}
        id={item.id}
        onClick={item.onClick}
        sx={item.destructive ? styles.destructiveItem : styles.item}
      >
        {item.label}
      </SystemDesignButton>
    ))}
  </Popover>
);

export default OverflowMenu;
